import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listGeoPolicies,
  upsertGeoPolicy,
  resetGeoPolicy,
  GEOGRAPHY_POLICIES,
  HIERARCHY_LEVELS,
  type EffectivePolicyRow,
} from '../../api/orgApi.js';

// Decides whether two holders of the same role may cover the same ground.
// A tenant may override the platform default per role; resetting removes the
// override rather than inventing a copy of the default.

const POLICY_HELP: Record<string, string> = {
  EXCLUSIVE: 'Two holders of this role cannot cover the same area.',
  SHARED: 'Several holders may cover the same area.',
  INHERITED: 'Area comes from whoever the holder reports to.',
  UNRESTRICTED: 'No geography limit applies.',
};

export function GeographyPolicyPage(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<EffectivePolicyRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, EffectivePolicyRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await listGeoPolicies(token);
      setRows([...r]);
      setDraft({});
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const edit = (row: EffectivePolicyRow, patch: Partial<EffectivePolicyRow>) => {
    setDraft((d) => ({ ...d, [row.roleCode]: { ...row, ...d[row.roleCode], ...patch } }));
  };

  const save = useCallback(
    async (row: EffectivePolicyRow) => {
      const next = draft[row.roleCode] ?? row;
      setBusy(row.roleCode);
      setNotice(null);
      try {
        await upsertGeoPolicy(token, row.roleCode, {
          functionalGroup: next.functionalGroup,
          hierarchyLevel: next.hierarchyLevel,
          geographyPolicy: next.geographyPolicy,
          maxUsersPerTenant: next.maxUsersPerTenant,
        });
        setNotice(`${row.roleCode} saved for this tenant.`);
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [draft, token, load],
  );

  const reset = useCallback(
    async (row: EffectivePolicyRow) => {
      setBusy(row.roleCode);
      setNotice(null);
      try {
        await resetGeoPolicy(token, row.roleCode);
        setNotice(`${row.roleCode} now follows the platform default.`);
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [token, load],
  );

  return (
    <main aria-label="geography-policy">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Geography Policy</h1>
      <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--color-text-muted)' }}>
        Whether two holders of the same role may cover the same area. Rows marked
        <strong> Platform </strong> use the default until you override them.
      </p>

      {error && (
        <div role="alert" data-testid="policy-error" style={{ color: 'var(--color-danger, #d32f2f)', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div role="status" data-testid="policy-notice" style={{ marginBottom: 12 }}>
          {notice}
        </div>
      )}

      {rows === null ? (
        <p data-testid="policy-loading">Loading…</p>
      ) : (
        <table data-testid="policy-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Role</th>
              <th style={{ textAlign: 'left' }}>Group</th>
              <th style={{ textAlign: 'left' }}>Level</th>
              <th style={{ textAlign: 'left' }}>Policy</th>
              <th style={{ textAlign: 'left' }}>Max users</th>
              <th style={{ textAlign: 'left' }}>Source</th>
              <th style={{ textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const cur = draft[row.roleCode] ?? row;
              return (
                <tr key={row.roleCode} data-testid={`policy-row-${row.roleCode}`}>
                  <td>{row.roleCode}</td>
                  <td>
                    <input
                      aria-label={`group-${row.roleCode}`}
                      value={cur.functionalGroup}
                      onChange={(e) => edit(row, { functionalGroup: e.target.value })}
                      style={{ width: 110 }}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={`level-${row.roleCode}`}
                      value={cur.hierarchyLevel}
                      onChange={(e) => edit(row, { hierarchyLevel: e.target.value })}
                    >
                      {HIERARCHY_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      aria-label={`policy-${row.roleCode}`}
                      value={cur.geographyPolicy}
                      onChange={(e) => edit(row, { geographyPolicy: e.target.value })}
                      title={POLICY_HELP[cur.geographyPolicy] ?? ''}
                    >
                      {GEOGRAPHY_POLICIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      aria-label={`max-${row.roleCode}`}
                      type="number"
                      min={1}
                      value={cur.maxUsersPerTenant ?? ''}
                      placeholder="no limit"
                      onChange={(e) =>
                        edit(row, {
                          maxUsersPerTenant: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>{row.source === 'TENANT' ? 'This tenant' : 'Platform'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      disabled={busy === row.roleCode}
                      data-testid={`save-${row.roleCode}`}
                      onClick={() => void save(row)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      disabled={busy === row.roleCode || row.source !== 'TENANT'}
                      title={row.source !== 'TENANT' ? 'Already following the platform default' : ''}
                      data-testid={`reset-${row.roleCode}`}
                      onClick={() => void reset(row)}
                    >
                      Reset
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
